import React from 'react';
import { Box, InputAdornment, SxProps, TextField, TextFieldProps, Theme, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

export interface FieldMessages {
  /** Wrong format: red, and saving is blocked. */
  error?: string | null;
  /** Missing for issuing: orange, saving is still allowed. */
  warning?: string | null;
  /** Checked and right, such as a bank account that passes model 97. */
  success?: string | null;
  /** What the field wants, shown while there is nothing else to say. */
  hint?: React.ReactNode;
  /** Shown instead of the hint's tone when the hint itself carries a warning. */
  hintTone?: 'warning';
}

interface FieldFrameProps extends FieldMessages {
  id: string;
  label: string;
  /** "Needed to issue an invoice", explained once above the form. */
  required?: boolean;
  /** Spans both columns of a section's grid. */
  wide?: boolean;
  /** Right-aligned beside the message line, e.g. "120 / 2000". */
  counter?: string;
  /** Set when the control is not a labelable element, such as a radiogroup. */
  labelId?: string;
  /** The label of a field that cannot be changed is quieter, like its value. */
  disabled?: boolean;
  sx?: SxProps<Theme>;
  children: React.ReactNode;
}

export const messageId = (id: string) => `${id}-message`;

const TONES = {
  error: { color: 'error.main', Icon: ErrorOutlineIcon },
  warning: { color: 'warning.main', Icon: WarningAmberIcon },
  success: { color: 'success.main', Icon: CheckCircleOutlineIcon }
} as const;

/**
 * A label above its control and one message line under it.
 *
 * The message line holds one thing at a time - the error, else the warning, else the
 * confirmation, else the hint - so a field never grows or jumps when it turns red: the
 * error takes the hint's place on the same line.
 */
export const FieldFrame: React.FC<FieldFrameProps> = ({
  id,
  label,
  required,
  wide,
  counter,
  labelId,
  error,
  warning,
  success,
  hint,
  hintTone,
  disabled,
  sx,
  children
}) => {
  const tone = error ? 'error' : warning ? 'warning' : success ? 'success' : hintTone === 'warning' ? 'warning' : null;
  const message = error || warning || success || hint;
  const Icon = tone && tone !== 'success' && (error || warning) ? TONES[tone].Icon : tone === 'success' ? TONES.success.Icon : null;

  return (
    <Box sx={{ gridColumn: wide ? '1 / -1' : undefined, minWidth: 0, ...(sx as object) }}>
      <Typography
        component="label"
        htmlFor={labelId ? undefined : id}
        id={labelId}
        sx={{ display: 'block', fontSize: 13, fontWeight: 500, color: disabled ? 'text.secondary' : 'text.primary', mb: 0.75 }}
      >
        {label}
        {required && (
          <Box component="span" aria-hidden sx={{ color: 'text.secondary', ml: 0.25 }}>
            *
          </Box>
        )}
      </Typography>
      {children}
      {(message || counter) && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mt: 0.5, minHeight: 17 }}>
          <Typography
            id={messageId(id)}
            component="div"
            sx={{ fontSize: 12, lineHeight: 1.4, color: tone ? TONES[tone].color : 'text.secondary', display: 'flex', gap: 0.5, alignItems: 'flex-start' }}
          >
            {Icon && <Icon aria-hidden sx={{ fontSize: 14, mt: '1px', flexShrink: 0 }} />}
            <span>{message}</span>
          </Typography>
          {counter && (
            <Typography aria-hidden sx={{ fontSize: 12, color: 'text.secondary', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
              {counter}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

type FormTextFieldProps = FieldMessages &
  Omit<TextFieldProps, 'error' | 'label' | 'id'> & {
    id: string;
    label: string;
    required?: boolean;
    wide?: boolean;
    /** Shows "n / max" and is the length the counter counts against. */
    maxLength?: number;
    /** A unit after the value, such as "dana"; the value is then right-aligned. */
    suffix?: string;
    /** Permanently fixed, e.g. a platform item's name: disabled, with a lock at the end. */
    locked?: boolean;
  };

/** The lock at the end of a field that can never be changed here. */
export const LockAdornment: React.FC = () => (
  <InputAdornment position="end">
    <LockOutlinedIcon aria-hidden sx={{ fontSize: 18, color: 'text.placeholder' }} />
  </InputAdornment>
);

/** The 44px field of the Finance forms. Required here marks, it does not enforce. */
export const FormTextField: React.FC<FormTextFieldProps> = ({
  id,
  label,
  required,
  wide,
  maxLength,
  suffix,
  locked,
  error,
  warning,
  success,
  hint,
  hintTone,
  multiline,
  slotProps,
  sx,
  ...rest
}) => {
  const disabled = !!rest.disabled || !!locked;
  const length = typeof rest.value === 'string' ? rest.value.length : 0;
  const describedBy = error || warning || success || hint ? messageId(id) : undefined;
  return (
    <FieldFrame
      id={id}
      label={label}
      required={required}
      wide={wide}
      error={error}
      warning={warning}
      success={success}
      hint={hint}
      hintTone={hintTone}
      disabled={disabled}
      counter={maxLength ? `${length} / ${maxLength}` : undefined}
    >
      <TextField
        id={id}
        fullWidth
        multiline={multiline}
        error={!!error}
        {...rest}
        disabled={disabled}
        slotProps={{
          ...slotProps,
          htmlInput: {
            'aria-invalid': !!error,
            'aria-describedby': describedBy,
            ...(suffix ? { style: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' } } : {}),
            ...((slotProps?.htmlInput as object) ?? {})
          },
          input: {
            endAdornment: locked ? (
              <LockAdornment />
            ) : success || suffix ? (
                <InputAdornment position="end">
                  {suffix && <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>{suffix}</Typography>}
                  {success && <CheckCircleOutlineIcon aria-hidden sx={{ fontSize: 20, color: 'success.main', ml: suffix ? 1 : 0 }} />}
                </InputAdornment>
              ) : undefined,
            ...((slotProps?.input as object) ?? {})
          }
        }}
        sx={{
          '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: 'surface.main', ...(multiline ? {} : { height: 44 }) },
          '& .MuiOutlinedInput-input': { fontSize: 15 },
          ...(sx as object)
        }}
      />
    </FieldFrame>
  );
};
